import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../config/api';
import { useAuth } from './AuthContext';

interface ActiveShift {
  id: number;
  clock_in: string;
  handoff_notes: string | null;
  alerts_sent: number;
  activities_logged: number;
}

interface CompletedShift extends ActiveShift {
  clock_out: string;
  duration_minutes: number;
}

interface ShiftContextType {
  activeShift: ActiveShift | null;
  loading: boolean;
  clockIn: () => Promise<void>;
  clockOut: (handoffNotes: string) => Promise<CompletedShift>;
  checkInToStore: (storeId: number) => Promise<void>;
  refreshShift: () => Promise<void>;
}

const ShiftContext = createContext<ShiftContextType | undefined>(undefined);

export function ShiftProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const [activeShift, setActiveShift] = useState<ActiveShift | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchCurrentShift = useCallback(async () => {
    try {
      const res = await api.get('/monitors/current-shift');
      setActiveShift(res.data.active_shift ?? null);
    } catch {
      setActiveShift(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user) {
      fetchCurrentShift();
    } else {
      setActiveShift(null);
      setLoading(false);
    }
  }, [user, fetchCurrentShift]);

  const clockIn = useCallback(async () => {
    const res = await api.post('/monitors/clock-in', {});
    setActiveShift(res.data);
  }, []);

  const clockOut = useCallback(async (handoffNotes: string): Promise<CompletedShift> => {
    const res = await api.post('/monitors/clock-out', {
      handoff_notes: handoffNotes || undefined,
    });
    setActiveShift(null);
    return res.data;
  }, []);

  const checkInToStore = useCallback(async (storeId: number) => {
    await api.post('/monitors/check-in', { store_id: storeId, notes: '' });
  }, []);

  const refreshShift = useCallback(async () => {
    await fetchCurrentShift();
  }, [fetchCurrentShift]);

  return (
    <ShiftContext.Provider
      value={{ activeShift, loading, clockIn, clockOut, checkInToStore, refreshShift }}
    >
      {children}
    </ShiftContext.Provider>
  );
}

export function useShift() {
  const ctx = useContext(ShiftContext);
  if (!ctx) throw new Error('useShift must be used within ShiftProvider');
  return ctx;
}
